import { analyticsHeaders } from "@/lib/analytics"

export function csrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")
  return meta?.content ?? ""
}

function requestHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return {
    Accept: "application/json",
    "X-CSRF-Token": csrfToken(),
    ...analyticsHeaders(),
    ...extra,
  }
}

export interface ApiErrorBody {
  error?: string
  message?: string
  max_length?: number
  upgrade_url?: string
  remaining?: number
  require_upgrade?: boolean
  require_sign_up?: boolean
  limit?: number
  used?: number
}

export interface ApiError extends Error {
  status: number
  body?: ApiErrorBody
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}`
  let body: ApiErrorBody | undefined
  try {
    body = (await response.json()) as ApiErrorBody
    if (body?.message) message = body.message
    else if (body?.error) message = body.error
  } catch {
    // non-JSON body; keep default message
  }
  const error = new Error(message) as ApiError
  error.status = response.status
  error.body = body
  return error
}

/** POST JSON to a Rails endpoint with CSRF protection. */
export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: requestHeaders({ "Content-Type": "application/json" }),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

/** POST multipart form data (for file uploads) with CSRF protection. */
export async function postForm<T>(url: string, formData: FormData): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: requestHeaders(),
    credentials: "same-origin",
    body: formData,
  })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

export async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: { Accept: "application/json", ...analyticsHeaders() },
    credentials: "same-origin",
  })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

export async function patchJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "PATCH",
    headers: requestHeaders({ "Content-Type": "application/json" }),
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

export async function deleteJson(url: string): Promise<void> {
  const response = await fetch(url, {
    method: "DELETE",
    headers: requestHeaders(),
    credentials: "same-origin",
  })
  if (!response.ok) throw await parseError(response)
}
